import { useCurrentOrganizationMembers } from "@api/organizationMember.api";
import { useIsVisibleAgentActor } from "@components/auditLog/hooks/useIsVisibleAgentActor";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { ActorIcon } from "@components/history/components/ActorIcon";
import { resolveActorDisplayName } from "@components/history/utils/actorUtils";
import type { HistoryRow } from "@components/history/views/historyViewConfig";
import { useUserStatus } from "@hooks/useUserStatus";
import styles from "./WorkActivity.module.scss";

/**
 * Who made an activity line's change: their actor icon (Kick, its agent, a
 * rule, an integration, the Platform API or the person), with their name in
 * the tooltip and for screen readers.
 */
export const WorkActivityActorAvatar = ({
    row,
}: Readonly<{ row: HistoryRow }>) => {
    const { data: userStatus } = useUserStatus();
    const { data: members } = useCurrentOrganizationMembers();
    const isAgentActor = useIsVisibleAgentActor(row.actorType);
    const member =
        row.actorId === null
            ? undefined
            : members?.find(({ user }) => user.id === row.actorId);
    const displayName = resolveActorDisplayName({
        actorType: row.actorType,
        actorName: row.actorName,
        sourceType: row.sourceType,
        fields: row.fields,
        changeTypes: row.changeTypes,
        system: row.system,
        actorId: row.actorId,
        isAgentActor,
        memberName: member?.user.preferredName,
        currentUserId: userStatus?.user.id,
    });

    return (
        <>
            <AvatarTooltip content={displayName}>
                <ActorIcon
                    actorType={row.actorType}
                    actorName={row.actorName}
                    sourceType={row.sourceType}
                    user={member?.user}
                    fields={row.fields}
                    changeTypes={row.changeTypes}
                    system={row.system}
                    isAgentActor={isAgentActor}
                    size="sm"
                />
            </AvatarTooltip>
            <span className={styles.visuallyHidden}>{displayName}</span>
        </>
    );
};
